import { Component, EventEmitter, Inject, OnDestroy, OnInit } from "@angular/core";

import { getFormulaForUnitType, getGeorasterUrl, getLocalUnitType, getNoDataValue, getUnitTypeBasedOnMetadata } from "@models/mood/helpers/dataset";

import { generateTimeSeriesSets, getDateFormat, createPeriodicity } from "@models/mood/helpers/timeseries";
import { TimeSeriesMetadata } from "@models/mood/metadata";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { LayerListService } from "src/app/core/layer-list.service";
import { VectorLayer } from "src/app/core/layers/vector-layer.class";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DatasetListingService } from "src/app/dataset-listing/dataset-listing.service";
import { add, differenceInSeconds, formatDistanceToNow, isAfter, isBefore, isSameDay, parse } from "date-fns";
import { Dataset } from "@models/mood/dataset";

import { FeatureCollection } from 'geojson';
import { TableConfiguration, TimeseriesExtractionDatasource, getEDEFrequency } from "./timeseries-extraction.datasource";
import { v4 } from "uuid";
import { Observable, map, timer } from "rxjs";
import { Router } from "@angular/router";
import { FeatureCounts, TableFeature, TableLayer } from "./base.datasource";
import { statKey } from "@models/websockets";

export interface TimeseriesDataExtractionComponentData {
  dataset_id: string;
}

@Component({
  selector: 'mood-timeseries-data-extraction-dialog',
  templateUrl: 'timeseries-data-extraction-dialog.component.html',
  styleUrls: ['timeseries-data-extraction-dialog.component.scss']
})
export class TimeseriesDataExtractionComponent implements OnInit, OnDestroy {
  public dataSource = new TimeseriesExtractionDatasource();

  public timeseriesParameterSelectionForm = new FormGroup({
    vector_layers: new FormControl<string[]>([], Validators.required),
    stats: new FormControl<statKey[]>([], Validators.required),
    start_date: new FormControl<Date | null>(null, Validators.required),
    end_date: new FormControl<Date | null>(null, Validators.required),
  });

  public get startDateControl() {
    return this.timeseriesParameterSelectionForm.get('start_date')!;
  }

  updateFormStartDate(value: Date | null) {
    this.startDateControl.setValue(value);
  }

  public get endDateControl() {
    return this.timeseriesParameterSelectionForm.get('end_date')!;
  }

  updateFormEndDate(value: Date | null) {
    this.endDateControl.setValue(value);
  }

  public get vectorLayersControl() {
    return this.timeseriesParameterSelectionForm.get('vector_layers')!;
  }

  public get statsControl() {
    return this.timeseriesParameterSelectionForm.get('stats')!;
  }

  public get applicableStats() {
    return this.statsControl.value?.filter(stat => !this.statsDisabled[stat]) || [];
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TimeseriesDataExtractionComponentData,
    public _dialogRef: MatDialogRef<TimeseriesDataExtractionComponent>,
    public _layerListService: LayerListService,
    private _dataListingService: DatasetListingService,
    private _router: Router
  ) { }

  private _timeseriesList: Date[] = [];

  minDate: Date | null = null;
  maxDate: Date | null = null;

  vectorLayers: VectorLayer[] = [];
  stats: statKey[] = ['identify', 'min', 'max', 'mean', 'median'];

  isLoaded = false;


  private _timeseriesDataset!: Dataset;

  async ngOnInit() {
    this.stats.forEach(stat => this.statsDisabled[stat] = true);

    this.timeseriesParameterSelectionForm.valueChanges.subscribe(values => {
      if (this.timeseriesParameterSelectionForm.valid) {
        this._calculateResultingDatapoints();
      }
    });

    this.vectorLayersControl.valueChanges.subscribe(vectorLayers => {
      const featureCounts = this._getFeatureCounts();

      Object.keys(this.statsDisabled).forEach(stat => {
        if(stat === 'identify') {
          this.statsDisabled[stat] = featureCounts.points === 0;
        } else {
          this.statsDisabled[stat] = featureCounts.shapes === 0;
        }
      });
    });

    this.vectorLayers = await this._layerListService.geojsonLayers;

    this._timeseriesDataset = (await this._dataListingService.findDatasetById(this.data.dataset_id))!;

    this.minDate = parse(this.datasetMetadata.start_date, this.datasetMetadata.date_formats!, new Date());
    this.maxDate = parse(this.datasetMetadata.end_date, this.datasetMetadata.date_formats!, new Date());

    let timeline = generateTimeSeriesSets(this.datasetMetadata);

    this._timeseriesList = timeline;

    this.isLoaded = true;
  }

  public statsDisabled: { [key: string]: boolean } = {}

  public exporting: EventEmitter<boolean> = new EventEmitter();

  async sendToEde() {
    this.exporting.emit(true);

    await this.dataSource.edeExport();

    this._router.navigate(['tool', 'epid-data-explorer']);
    this._dialogRef.close();

    this.exporting.emit(false);
  }

  async exportData() {
    this.exporting.emit(true);

    await this.dataSource.downloadExport();

    this.exporting.emit(false);
  }

  public get datasetName() {
    return this._timeseriesDataset?.name;
  }

  private get datasetMetadata(): TimeSeriesMetadata {
    return this._timeseriesDataset!.metadata as TimeSeriesMetadata;
  }

  timeseriesFilter = (d: Date | null): boolean => {
    return this._timeseriesList.some(date => date.getTime() == d!.getTime());
  };


  public amoutOfCalculations: number = 0;


  private get filteredTimeseriesList() {
    return this._timeseriesList.filter(date =>
      (isAfter(date, this.startDateControl.value!) || isSameDay(date, this.startDateControl.value!)) &&
      (isBefore(date, this.endDateControl.value!) || isSameDay(date, this.endDateControl.value!))
    );
  }

  private _calculateResultingDatapoints() {
    const featureCounts = this._getFeatureCounts();
    const selectedStats = this.statsControl.value!;

    const pointFeatureCalculations = selectedStats.includes("identify") ? featureCounts.points : 0;
    const otherFeaturesCalculations = selectedStats.filter(stat => stat !== "identify").length * featureCounts.shapes;

    const filteredList = this.filteredTimeseriesList;
    this.amoutOfCalculations = filteredList.length * (pointFeatureCalculations + otherFeaturesCalculations)
  }

  private _getFeatureCounts(): FeatureCounts {
    const selectedVectorLayers = this.vectorLayers.filter(vl => this.vectorLayersControl.value!.includes(vl.unique_id));
    const allSelectedFeatures = selectedVectorLayers.flatMap(layer => (layer.data as FeatureCollection).features!);

    const pointFeaturesCount = allSelectedFeatures.filter(feature => feature.geometry.type === "Point").length;
    const otherFeaturesCount = allSelectedFeatures.length - pointFeaturesCount;

    return { total: allSelectedFeatures.length, points: pointFeaturesCount, shapes: otherFeaturesCount };
  }

  extractionStarted: boolean = false;

  generateTableStructure() {
    const filteredList = this.filteredTimeseriesList;

    filteredList.forEach(layer => {
      const tableLayer: TableLayer = {
        layer_id: layer.getTime().toString(),
        raster_url: getGeorasterUrl(this._timeseriesDataset, layer.getTime().toString())!
      };
      this.dataSource.addLayer(tableLayer);
    });

    const allSelectedFeatures = this.vectorLayers.filter(vl => this.vectorLayersControl.value!.includes(vl.unique_id)).flatMap(layer => (layer.data as FeatureCollection).features);

    allSelectedFeatures.forEach(feature => {
      const tableFeature: TableFeature = {
        feature_id: v4(),
        feature: feature,
        properties: feature.properties || {}
      };
      this.dataSource.addFeature(tableFeature);
    });

    const datasetUnitType = getUnitTypeBasedOnMetadata(this._timeseriesDataset);
    const periodicity = createPeriodicity(this.datasetMetadata.periodicity_days, this.datasetMetadata.periodicity_months, this.datasetMetadata.periodicity_weeks, this.datasetMetadata.periodicity_days);
    const tableConfig: TableConfiguration = {
      feature_stats: this.applicableStats,
      layer_name: this._timeseriesDataset.name,
      unit: getLocalUnitType(datasetUnitType, this._timeseriesDataset),
      formula: getFormulaForUnitType(datasetUnitType, this._timeseriesDataset),
      no_data: getNoDataValue(this._timeseriesDataset),
      frequency: getEDEFrequency(periodicity),
      date_format: getDateFormat(periodicity)
    };

    this.dataSource.setTableConfiguration(tableConfig);


    this.dataSource.startDataExtraction();
    this._startProgressUpdated();


    this.extractionStarted = true;
  }


  ngOnDestroy(): void {
    this.dataSource.destroy();
  }

  //#region Progress Updates
  private baseIntervalTimer?: Observable<number>;

  public progressObservableObject?: Observable<{
    progress: number,
    finishedDataPoints: number,
    timeRunning: string,
    calculationsPerSecond: number,
    estimatedTimeLeft: string,
    estimatedEndTime: Date
  }>;

  private _startProgressUpdated() {
    this.baseIntervalTimer = timer(0, 1000);

    this.progressObservableObject = this.baseIntervalTimer.pipe(
      map(() => {
        const secondsTaken = differenceInSeconds(new Date(), this.dataSource.startTime!);
        const calculationsPerSecond = (this.dataSource.finishedDatapoints / secondsTaken) || 50; // Default of +- 50 calculations per seconds, fixed the Infinity date will still using a decent average from actual extractions
        const estimatedTimeLeft = (this.dataSource.totalDataPoints - this.dataSource.finishedDatapoints) / calculationsPerSecond;
        const estimatedEndTime = add(new Date(), { seconds: estimatedTimeLeft });

        return {
          progress: Math.round(this.dataSource.progress * 100) / 100,
          finishedDataPoints: this.dataSource.finishedDatapoints,
          timeRunning: formatDistanceToNow(this.dataSource.startTime!, { includeSeconds: true }),
          calculationsPerSecond: Math.round(calculationsPerSecond),
          estimatedTimeLeft: formatDistanceToNow(estimatedEndTime),
          estimatedEndTime: estimatedEndTime
        }
      })
    )
  }
  //#endregion
}
